import React from "react";
import { motion } from "motion/react";
import { MapPin, Clock, Users, Phone, ArrowRight } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { suites, WHATSAPP_LINK } from "../data/mock";

const SuitesSection = () => {
  const getWhatsAppUrl = (suiteName) => {
    const message = `Olá! Tenho interesse na ${suiteName}. Gostaria de mais informações.`;
    return `${WHATSAPP_LINK}?text=${encodeURIComponent(message)}`;
  };

  return (
    <section id="suites" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[hsl(var(--coral))] text-sm font-semibold uppercase tracking-wider mb-4">
            Hospedagem Premium
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Suítes no Rio de Janeiro
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Acomodações selecionadas nos melhores bairros da cidade maravilhosa.
            Conforto e localização privilegiada.
          </p>
        </motion.div>

        {/* Suites Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {suites.map((suite, index) => (
            <motion.div
              key={suite.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="group h-full overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-shadow duration-300">
                <div className="relative overflow-hidden">
                  <img
                    src={suite.image}
                    alt={suite.name}
                    className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 text-white/90 text-sm">
                      <MapPin className="w-4 h-4" />
                      <span>{suite.location}</span>
                    </div>
                  </div>
                </div>
                <CardContent className="p-5">
                  <h3 className="font-display text-lg font-semibold text-gray-900 mb-2">
                    {suite.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {suite.description}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{suite.ideal}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-gray-500 mb-5">
                    <Clock className="w-4 h-4" />
                    <span>{suite.duration}</span>
                  </div>
                  <motion.a
                    href={getWhatsAppUrl(suite.name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button className="w-full bg-[hsl(var(--whatsapp))] hover:bg-[hsl(142_69%_38%)] text-white rounded-full py-3 h-auto font-medium gap-2">
                      <Phone className="w-4 h-4" />
                      Reservar no WhatsApp
                    </Button>
                  </motion.a>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-12"
        >
          <p className="text-gray-600 mb-4">
            Não encontrou o que procura? Montamos pacotes personalizados!
          </p>
          <motion.a
            href={`${WHATSAPP_LINK}?text=${encodeURIComponent("Olá! Gostaria de um pacote de hospedagem personalizado.")}`}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="inline-flex items-center gap-2 text-[hsl(var(--coral))] font-semibold hover:underline"
          >
            Fale com nossa equipe
            <ArrowRight className="w-4 h-4" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default SuitesSection;
